import { Controller } from "@hotwired/stimulus"

// The server compares a typed value after Ruby's String#strip, which trims only these characters (not a
// non-breaking space), and skips a value Rails calls blank. The labels here follow the same rules.
const RUBY_STRIP = /^[ \t\n\v\f\r\0]+|[ \t\n\v\f\r\0]+$/g
const rubyStrip = (value) => value.replace(RUBY_STRIP, "")
const railsBlank = (value) => /^\s*$/.test(value)

// The review gate's queue. The plan is rendered server-side; this controller only does
// what needs a browser: keeps one item open at a time, cuts the region out of the page
// image already loaded beside it, follows the selection onto the right document and
// page, records each call as it is made, and holds Approve back until every finding is
// settled. Nothing here decides anything — the server refuses an early approval too.
// A clearance question is a small form: a radio picks the answer ("confirmed" or
// "flagged") and "no" shows the field for the right value. What is picked counts at once: the
// step takes its check and Finish wakes up. Finish sends every pick that is not saved yet and then
// completes the task, so the person never presses a save first. When the answers are optional (a
// review clearance task) Finish is always ready: it asks only for one answer or a note.
export default class extends Controller {
  static targets = ["payload", "item", "mark", "open", "trail", "saved", "fixForm", "cuttings",
                    "progress", "bar", "done", "folded",
                    "page", "pageName", "pageOf", "pageDots", "scroller", "sheet"]
  // inline: the review sits on a task card in a list or a timeline. It takes no keys
  // (another review may be open in the task) and does not scroll the page to itself.
  // optional: the answers are helpers, and Finish never waits for them.
  static values = { required: Number, multi: Boolean, inline: Boolean, optional: Boolean }

  // A cutting is sized for reading: a line of small print comes out around this tall,
  // while a stamp or a signature is already big enough and is shown at its own size.
  static READABLE_HEIGHT = 34
  static MAX_ZOOM = 3

  connect() {
    const data = JSON.parse(this.payloadTarget.textContent)
    this.items = new Map(data.items.map((item) => [item.id, item]))
    this.sources = new Map(data.sources.map((source) => [source.key, source]))
    this.decideUrl = data.decide_url
    this.planVersion = data.plan_version
    this.decisions = new Map(Object.entries(data.decisions || {}))
    this.images = new Map()
    this.sel = null

    this.keydown = this.keydown.bind(this)
    if (!this.inlineValue) document.addEventListener("keydown", this.keydown)

    // Finish is only hooked from this task's own fieldset: a timeline holds many tasks' buttons.
    this.finish = this.finish.bind(this)
    this.finishButton = this.approveButton(this.element.closest("fieldset"))
    this.finishButton?.addEventListener("click", this.finish, true)

    this.refresh()
    const first = this.itemTargets.find((el) => el.dataset.required === "true" && !this.decisions.has(el.dataset.itemId))
      || this.itemTargets[0]
    if (first) this.open(first, !this.inlineValue)
  }

  disconnect() {
    document.removeEventListener("keydown", this.keydown)
    this.finishButton?.removeEventListener("click", this.finish, true)
  }

  // ── selection ──────────────────────────────────────────────────────────────────

  // Pressing the open step's title folds it away; pressing another opens that one.
  select(event) {
    const el = event.currentTarget.closest("[data-item-id]")
    if (el === this.sel && !el.querySelector("[data-review-queue-target='open']").hidden) this.close(el)
    else this.open(el)
  }

  close(el) {
    el.classList.remove("is-on")
    el.querySelector("[data-review-queue-target='open']").hidden = true
    if (this.sel === el) this.sel = null
  }

  open(el, scroll = true) {
    if (!el) return
    this.itemTargets.forEach((other) => {
      const on = other === el
      other.classList.toggle("is-on", on)
      other.querySelector("[data-review-queue-target='open']").hidden = !on
    })
    this.sel = el
    if (el.closest("[data-folded='true']")) this.unfold()
    this.cut(el)
    this.showPage(el)
    if (scroll) el.scrollIntoView({ block: "nearest" })
  }

  // After an answer, the next question still to answer opens (wrapping to an earlier one).
  // When none is left, the answered one folds away so the finish button is next.
  next(onlyOpen = true) {
    const list = this.itemTargets
    const from = list.indexOf(this.sel)
    const after = list.slice(from + 1)
    if (!onlyOpen) {
      if (after[0]) this.open(after[0])
      return
    }
    const waiting = (el) => el.dataset.required === "true" && !this.answered(el)
    const target = after.find(waiting) || list.slice(0, Math.max(from, 0)).find(waiting)
    if (target) this.open(target)
    else if (this.sel) this.close(this.sel)
  }

  prev() {
    const list = this.itemTargets
    const from = list.indexOf(this.sel)
    if (from > 0) this.open(list[from - 1])
  }

  unfold() {
    if (!this.hasFoldedTarget) return
    const band = this.foldedTarget
    const folded = band.dataset.folded !== "true"
    band.dataset.folded = folded
    band.querySelector(".review-queue-list").hidden = folded
    band.querySelector("[aria-expanded]").setAttribute("aria-expanded", String(!folded))
  }

  // ── decisions ──────────────────────────────────────────────────────────────────

  // An item with one-click answers is confirmed only through the answer that keeps it as it is.
  confirm(event) {
    event?.stopPropagation()
    if (!this.sel || this.busy) return
    const choices = [...this.sel.querySelectorAll("[data-choice]")]
    if (choices.length) {
      const keep = choices.find((button) => button.dataset.decision === "confirmed")
      if (keep) keep.click()
      else this.flash(this.sel, "Pick what to enter")
      return
    }
    this.decide(this.sel, { decision: "confirmed" })
  }

  // One of the item's one-click answers: the server records it as its plan holds it.
  take(event) {
    event?.stopPropagation()
    const button = event.currentTarget
    const el = button.closest("[data-item-id]")
    if (!el || this.busy) return
    this.decide(el, { decision: button.dataset.decision, choice: button.dataset.choice })
  }

  fix(event) {
    event?.stopPropagation()
    if (!this.sel) return
    const no = this.sel.querySelector("[data-review-answer][value='flagged']")
    if (no) {
      no.checked = true
      this.chosen(this.sel)
      return
    }
    const form = this.sel.querySelector("[data-review-queue-target='fixForm']")
    if (!form) return
    form.hidden = false
    form.querySelector("input, textarea")?.focus()
  }

  // A possible item was picked. When it is the item's only question ("save") it is saved as the correction at
  // once; otherwise Fix it opens with that item typed in, ready to save.
  pick(event) {
    event?.stopPropagation()
    const button = event.currentTarget
    const el = button.closest("[data-item-id]")
    if (el && button.dataset.save === "true") {
      if (!this.busy) this.decide(el, { decision: "flagged", values: { [button.dataset.field]: button.dataset.value }, ...this.remembered(el) })
      return
    }
    const form = el?.querySelector("[data-review-queue-target='fixForm']")
    const input = form?.querySelector(`input[name='values[${CSS.escape(button.dataset.field)}]']`)
    if (!input) return
    input.value = button.dataset.value
    form.hidden = false
    form.querySelector(".btn-primary-action")?.focus()
  }

  // A clearance answer was picked: "no" shows the field for the right value, and the pick counts toward Finish.
  choose(event) {
    this.chosen(event.currentTarget.closest("[data-item-id]"), true)
  }

  chosen(el, focus = true) {
    const picked = el.querySelector("[data-review-answer]:checked")
    const form = el.querySelector("[data-review-queue-target='fixForm']")
    const fixing = picked?.value === "flagged"
    if (form) form.hidden = !fixing
    if (fixing && focus) form.querySelector("input, textarea")?.focus()
    this.refresh()
  }

  // ── a question's answer, as it stands on screen ──────────────────────────────────

  hasAnswerInputs(el) {
    return Boolean(el.querySelector("[data-review-answer]"))
  }

  // The call the server would record for the question now: null when nothing is picked.
  answerFor(el) {
    const picked = el.querySelector("[data-review-answer]:checked")
    if (!picked) return null
    if (picked.value === "confirmed") return { decision: "confirmed" }
    const form = el.querySelector("[data-review-queue-target='fixForm']")
    if (!form) return null
    const values = {}
    form.querySelectorAll("input[name^='values[']").forEach((input) => { values[input.name.slice(7, -1)] = input.value })
    return { decision: "flagged", values, note: form.querySelector("[name='note']")?.value ?? "" }
  }

  // "No" with no value typed says nothing yet, and the server refuses it.
  ready(answer) {
    return answer.decision === "confirmed" || Object.values(answer.values).some((value) => !railsBlank(value))
  }

  readyAnswer(el) {
    const answer = this.answerFor(el)
    return answer && this.ready(answer) ? answer : null
  }

  // Whether the answer on screen is the one the server already holds, so Finish has nothing to send for it.
  matchesSaved(el, answer) {
    const saved = this.decisions.get(el.dataset.itemId)
    if (!saved || saved.decision !== answer.decision) return false
    if (answer.decision === "confirmed") return true
    const values = saved.values || {}
    return Object.entries(answer.values).every(([name, typed]) => typed === (values[name] ?? "")) &&
      rubyStrip(answer.note) === (saved.note || "")
  }

  // A plain finding is answered by its saved call. A question is answered by what is picked on it now,
  // saved or not: Finish sends it.
  answered(el) {
    if (this.hasAnswerInputs(el)) return Boolean(this.readyAnswer(el))
    return this.decisions.has(el.dataset.itemId)
  }

  unanswered() {
    return this.itemTargets.filter((el) => el.dataset.required === "true" && !this.answered(el))
  }

  // The questions the server holds an answer for that the person has since unpicked: Finish takes those back.
  withdrawn() {
    return this.itemTargets.filter((el) => this.hasAnswerInputs(el) && this.decisions.has(el.dataset.itemId) &&
      !el.querySelector("[data-review-answer]:checked"))
  }

  // The questions whose answer is picked and complete but not saved yet.
  unsaved() {
    return this.itemTargets.filter((el) => {
      const answer = this.readyAnswer(el)
      return answer && !this.matchesSaved(el, answer)
    })
  }

  // Enter in an answer (outside the note) opens the next question; Finish sends what is picked.
  submit(event) {
    if (event.type === "keydown" && event.target.tagName === "TEXTAREA") return
    event.preventDefault()
    event.stopPropagation()
    this.next()
  }

  // Finish sends the answers that are picked but not saved, in order, and then completes the task. The button is
  // never disabled while they go (a disabled submitter's name and value are not posted), and a failed save
  // stops the task from completing and opens the question that failed.
  async finish(event) {
    if (this.finishing) return event.preventDefault()
    if (this.optionalValue && this.saysNothing()) {
      event.preventDefault()
      this.holdFinish()
      return
    }
    if (!this.busy && !this.unsaved().length && !this.withdrawn().length) return
    event.preventDefault()
    const button = event.currentTarget
    this.finishing = true
    button.classList.add("is-pressed")
    try {
      while (this.busy) await new Promise((resolve) => setTimeout(resolve, 25))
      for (const el of this.withdrawn()) {
        if (!await this.withdraw(el)) {
          this.open(el)
          return
        }
      }
      for (const el of this.unsaved()) {
        if (!await this.decide(el, this.answerFor(el), false)) {
          this.open(el)
          return
        }
      }
      button.form.requestSubmit(button)
    } finally {
      this.finishing = false
      button.classList.remove("is-pressed")
    }
  }

  cancelFix(event) {
    event?.stopPropagation()
    event.currentTarget.closest(".review-fix").hidden = true
  }

  typedValues(form) {
    return [...form.querySelectorAll("input[name^='values[']")]
      .map((input) => ({ typed: rubyStrip(input.value), onFile: input.dataset.onFile ?? "" }))
  }

  corrects(form) {
    return this.typedValues(form).some(({ typed, onFile }) => !railsBlank(typed) && typed !== onFile)
  }

  saveFix(event) {
    // Enter inside the note should still make a new line.
    if (event.type === "keydown" && event.target.tagName === "TEXTAREA") return
    event.preventDefault()
    if (this.busy) return
    const form = event.currentTarget.closest(".review-fix") || event.currentTarget
    this.sendFix(form).then((ok) => { if (ok) form.hidden = true })
  }

  sendFix(form) {
    const el = form.closest("[data-item-id]")
    const values = {}
    form.querySelectorAll("input[name^='values[']").forEach((input) => {
      values[input.name.slice(7, -1)] = input.value
    })
    return this.decide(el, { decision: "flagged", values, note: form.querySelector("[name='note']").value, ...this.remembered(el) })
  }

  // "Remember for this customer", sent only where the item offers it.
  remembered(el) {
    const box = el.querySelector("input[name='remember']")
    return box ? { remember: box.checked } : {}
  }

  // One decision at a time. A reviewer leaning on Enter would otherwise confirm the same
  // item once per keypress while the first request was still in flight, and each answer
  // would step the cursor past a finding nobody settled.
  async decide(el, body, advance = true) {
    if (this.busy) return false
    this.busy = true
    try {
      return await this.send(el, body, advance)
    } catch (_error) {
      this.flash(el, "Couldn't save that — check your connection and try again")
      return false
    } finally {
      this.busy = false
    }
  }

  async send(el, body, advance = true) {
    const id = el.dataset.itemId
    const response = await fetch(this.decideUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json",
                 "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
      body: JSON.stringify({ item_id: id, plan_version: this.planVersion, ...body })
    })
    const json = await response.json().catch(() => ({}))
    if (!response.ok || !json.ok) {
      this.flash(el, json.error || "Couldn't save that — try again")
      return false
    }
    this.decisions.set(id, json.decision)
    el.dataset.decided = json.decision.decision
    this.paint(el)
    this.refresh()
    if (advance) this.next()
    return true
  }

  // Takes back an answer the server holds, so Finish settles only what is picked on screen.
  async withdraw(el) {
    const id = el.dataset.itemId
    try {
      const response = await fetch(this.decideUrl, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
                   "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
        body: JSON.stringify({ item_id: id, plan_version: this.planVersion })
      })
      const json = await response.json().catch(() => ({}))
      if (!response.ok || !json.ok) {
        this.flash(el, json.error || "Couldn't take that answer back — try again")
        return false
      }
    } catch (_error) {
      this.flash(el, "Couldn't take that answer back — check your connection and try again")
      return false
    }
    this.decisions.delete(id)
    delete el.dataset.decided
    return true
  }

  // The step's check and its trail say what is picked on a question, saved or not, and what was saved on a plain finding.
  paint(el) {
    const question = this.hasAnswerInputs(el)
    const decided = question ? this.readyAnswer(el) : this.decisions.get(el.dataset.itemId)
    const mark = el.querySelector("[data-review-queue-target='mark']")
    const trail = el.querySelector("[data-review-queue-target='trail']")
    // Only a plain finding has one-click answers (item["choices"]).
    const chosen = !question && this.paintChoices(el, decided)
    if (chosen) {
      // A one-click answer is a decision, not a correction: the step is checked and says which.
      if (mark) mark.dataset.state = "matched"
      if (trail) trail.textContent = chosen.dataset.done
    } else if (decided?.decision === "confirmed") {
      if (mark) mark.dataset.state = "matched"
      if (trail) trail.textContent = el.dataset.confirmedTrail || "kept"
    } else if (decided?.decision === "flagged") {
      if (mark) mark.dataset.state = "rejected"
      if (trail) {
        const form = el.querySelector("[data-review-queue-target='fixForm']")
        // Typing the value already saved keeps it: a clearance question says it kept the value with a note.
        const noted = el.dataset.notedTrail !== undefined && form && !this.corrects(form)
        trail.textContent = noted ? (el.dataset.notedTrail || "noted") : (el.dataset.fixedTrail || "fixed")
      }
    } else if (question) {
      if (mark) mark.dataset.state = "held"
      if (trail) trail.textContent = ""
    }
  }

  // Marks the saved one of an item's one-click answers, and returns it.
  paintChoices(el, decided) {
    const buttons = [...el.querySelectorAll("[data-choice]")]
    buttons.forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.choice === decided?.choice)))
    return buttons.find((button) => button.dataset.choice === decided?.choice)
  }

  // What changed on screen: the questions' steps, the count, and Finish.
  refresh() {
    this.itemTargets.filter((el) => this.hasAnswerInputs(el)).forEach((el) => this.paint(el))
    this.paintProgress()
    this.gateApprove()
  }

  paintProgress() {
    const required = this.requiredValue
    const settled = required - this.unanswered().length
    if (this.hasBarTarget) this.barTarget.style.width = (required ? settled / required * 100 : 100) + "%"
    if (this.hasDoneTarget) this.doneTarget.textContent = settled
    if (this.hasProgressTarget) this.progressTarget.dataset.settled = String(settled >= required)
  }

  approveButton(scope) {
    return scope && [...scope.querySelectorAll(".resolution-option")]
      .find((button) => /^complete/.test(button.value || ""))
  }

  noteField() {
    return (this.element.closest("fieldset") || document).querySelector("textarea[name='task_action[resolution_notes]']")
  }

  // Finish with optional answers needs one answer, picked or saved, or a note; otherwise it would say nothing.
  saysNothing() {
    const picked = this.itemTargets.some((el) => this.answered(el) || this.decisions.has(el.dataset.itemId))
    return !picked && railsBlank(this.noteField()?.value ?? "")
  }

  // Says why Finish did not go, beside it, and puts the cursor in the note; typing there clears it.
  holdFinish() {
    const scope = this.element.closest("fieldset") || document
    scope.querySelectorAll("[data-review-held-note]").forEach((note) => { note.hidden = false })
    const field = this.noteField()
    if (!field) return
    field.focus()
    field.addEventListener("input", () => this.gateApprove(), { once: true })
  }

  // The Approve chip lives in the task's own resolution composer, outside this element.
  // It stays in view, disabled beside a short reason, until every finding has an answer
  // (picked on screen or saved), so the way to finish is never a mystery. Finish saves the
  // picks it still holds. The server refuses an early approval as well.
  gateApprove() {
    const scope = this.element.closest("fieldset") || document
    const approve = this.approveButton(scope)
    if (!approve) return
    // Optional answers never hold Finish back; the held note shows only when Finish is pressed with nothing said.
    const left = this.optionalValue ? 0 : this.unanswered().length
    approve.disabled = left > 0
    approve.hidden = false
    if (left === 0) approve.removeAttribute("aria-describedby")
    scope.querySelectorAll("[data-review-held-note]").forEach((note) => { note.hidden = left === 0 })
  }

  // An error holds the person's attention longer than a note: it can stop Finish.
  flash(el, text, ms = 6000) {
    const note = el.querySelector("[data-review-queue-target='saved']")
    if (!note) return
    note.textContent = text
    note.hidden = false
    clearTimeout(note._timer)
    note._timer = setTimeout(() => { note.hidden = true }, ms)
  }

  // ── the page ───────────────────────────────────────────────────────────────────

  imageFor(sourceKey, page) {
    const key = `${sourceKey}#${page || 1}`
    if (this.images.has(key)) return this.images.get(key)
    const source = this.sources.get(sourceKey)
    const url = source?.urls?.[(page || 1) - 1] || source?.urls?.[0]
    if (!url) return null
    const img = new Image()
    img.src = url
    this.images.set(key, img)
    return img
  }

  regionsOf(el) {
    const item = this.items.get(el.dataset.itemId)
    if (!item) return []
    if (item.cites?.length > 1) return item.cites.filter((c) => c.box).map((c) => [c.source, c.box])
    return item.box ? [[item.source, item.box]] : []
  }

  showPage(el) {
    // No page column when no finding is marked on the page.
    if (!this.hasPageTarget) return
    const item = this.items.get(el.dataset.itemId)
    if (!item) return
    const [key, box] = this.regionsOf(el)[0] || [item.cites?.[0]?.source || item.source, null]
    const source = this.sources.get(key) || [...this.sources.values()][0]
    if (!source) return
    const page = box?.page || 1
    const img = this.imageFor(source.key, page)
    if (img && this.pageTarget.src !== img.src) this.pageTarget.src = img.src

    this.pageNameTarget.textContent = this.multiValue ? (source.label || source.key) : ""
    this.pageOfTarget.textContent = source.pages > 1 ? `page ${page} of ${source.pages}` : ""
    this.pageDotsTarget.replaceChildren()
    if (source.pages > 1) {
      for (let p = 1; p <= source.pages; p++) {
        const dot = document.createElement("b")
        if (p === page) dot.className = "is-on"
        this.pageDotsTarget.appendChild(dot)
      }
    }
    this.highlight(source.key, page, box)
  }

  highlight(sourceKey, page, current) {
    this.sheetTarget.querySelectorAll(".review-hl").forEach((n) => n.remove())
    this.itemTargets.forEach((el) => {
      this.regionsOf(el).forEach(([key, box]) => {
        if (key !== sourceKey || (box.page || 1) !== page) return
        const hl = document.createElement("span")
        const decided = this.decisions.get(el.dataset.itemId)?.decision
        hl.className = "review-hl " + (el === this.sel ? "is-on" : decided === "confirmed" ? "is-done" : "")
        hl.style.cssText = `left:${box.x1 * 100}%;top:${box.y1 * 100}%;width:${(box.x2 - box.x1) * 100}%;height:${(box.y2 - box.y1) * 100}%`
        this.sheetTarget.appendChild(hl)
      })
    })
    if (current) {
      const place = () => this.scrollerTarget.scrollTo({ top: current.y1 * this.pageTarget.clientHeight - 140, behavior: "smooth" })
      this.pageTarget.complete ? place() : this.pageTarget.addEventListener("load", place, { once: true })
    }
  }

  // ── cuttings ───────────────────────────────────────────────────────────────────

  cut(el) {
    el.querySelectorAll(".review-cutting").forEach((figure) => {
      if (figure.dataset.done) return
      figure.dataset.done = "1"
      const box = JSON.parse(figure.dataset.box || "null")
      const img = box && this.imageFor(figure.dataset.source, box.page)
      if (!img) return
      const draw = () => this.drawCutting(figure, box, img)
      img.complete && img.naturalWidth ? draw() : img.addEventListener("load", draw, { once: true })
    })
  }

  // Padding is proportional to the region, never a fixed slice of the page: a fixed one
  // is half a line on a small field and drags the line above into the cutting.
  drawCutting(figure, box, img) {
    const py = (box.y2 - box.y1) * 0.13, px = (box.x2 - box.x1) * 0.02
    const sx = Math.max(0, box.x1 - px) * img.naturalWidth
    const sy = Math.max(0, box.y1 - py) * img.naturalHeight
    const sw = Math.min(1, (box.x2 - box.x1) + px * 2) * img.naturalWidth
    const sh = Math.min(1, (box.y2 - box.y1) + py * 2) * img.naturalHeight
    if (sw < 1 || sh < 1) return
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const canvas = document.createElement("canvas")
    canvas.width = Math.round(sw * dpr); canvas.height = Math.round(sh * dpr)
    canvas.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height)
    const room = (figure.closest(".review-item__open")?.clientWidth || sw) - 48
    const scale = Math.max(1, Math.min(this.constructor.MAX_ZOOM, this.constructor.READABLE_HEIGHT / sh, room / sw))
    canvas.style.width = Math.round(Math.max(28, sw * scale)) + "px"
    figure.querySelector(".review-cutting__paper").replaceChildren(canvas)
  }

  // ── keys ───────────────────────────────────────────────────────────────────────

  keydown(event) {
    if (event.metaKey || event.ctrlKey || event.altKey) return
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName) || event.target.isContentEditable) {
      // A clearance answer keeps its field while "no" is picked; Escape only closes the generic fix form.
      if (event.key === "Escape" && !event.target.closest(".review-answer")) event.target.closest(".review-fix")?.setAttribute("hidden", "")
      return
    }
    const key = event.key.toLowerCase()
    if (key === "j" || event.key === "ArrowDown") this.next(false)
    else if (key === "k" || event.key === "ArrowUp") this.prev()
    else if (event.key === "Enter" && this.sel?.querySelector("[data-review-answer]:checked")) this.next()
    else if (event.key === "Enter" && this.sel) this.confirm()
    else if (key === "x" && this.sel) this.fix()
    else return
    event.preventDefault()
  }
}
